<script lang="ts" setup>
import Button from 'primevue/button'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import TableFilter from '@/components/table/TableFilter.vue'
import TableSearchInput from '@/components/table/TableSearchInput.vue'
import { onMounted, ref, computed } from 'vue'
import { Icon } from '@iconify/vue'
import { RouterLink, useRouter } from 'vue-router'
import { getArchivedMerges } from '@/api/yarns'
import type { Supplier } from '@/types/supplier-types'
import { getSupplierList } from '@/api/suppliers'
import { getYarnType } from '@/constants/yarns'
import { getSupplier } from '@/constants/suppliers'

const router = useRouter()
const searchQuery = ref('')
const loading = ref<boolean>(false)
const yarnList = ref<any>([])
const suppliers = ref<Supplier[]>()

const filteredYarnList = computed(() => {
    const query = searchQuery.value.toLowerCase()
    return yarnList.value.filter((yarn: any) =>
        yarn.mergeNumber.toLowerCase().includes(query) ||
        getYarnType(yarn.yarnType)?.toLowerCase().includes(query) ||
        getSupplier(yarn.supplierId, suppliers.value)?.toLowerCase().includes(query) ||
        yarn.yarnCount.toLowerCase().includes(query)
    )
})

onMounted(async () => {
    loading.value = true
    try {
        yarnList.value = await getArchivedMerges()
        suppliers.value = await getSupplierList()
    } catch (error) {
        console.error('Failed to load archived merges:', error)
    } finally {
        loading.value = false
    }
})
</script>

<template>
  <div class="flex justify-start py-2">
        <RouterLink to="/yarns"
        class="flex items-center gap-x-2 group hover:text-primary-default">
            <Icon
            icon="material-symbols:arrow-back-rounded"
            class="text-2xl group-hover:translate-x-2 transition-transform text-primary-500"
          />
          <span>Back</span>
        </RouterLink>
    </div>
    <div class="flex flex-wrap items-end justify-between gap-2 mb-6">
    <div>
      <h1 class="text-3xl font-semibold tracking-tighter">Archived Yarns</h1>
      <p class="max-w-sm text-sm text-neutral-500 font-normal mt-1" >
        Total: {{ yarnList.length }}
      </p>
    </div>
  </div>
  <DataTable
    :value="filteredYarnList"
    paginator
    :rows="10"
    tableStyle="min-width: 50rem"
    stripedRows
    paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
    :rowsPerPageOptions="[10, 25]"
    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} suppliers"
    :loading="loading"
    :pt="{
      table: loading && 'min-h-[30rem]'
    }"
    :pt-options="{
      mergeProps: true
    }"
  >
    <template #empty> <p class="text-center">No yarns found.</p> </template>

    <template #header>
      <div class="flex items-center justify-between font-normal">
        <TableSearchInput v-model:searchQuery="searchQuery" />

        <TableFilter />
      </div>
    </template>

    <Column field="merge" header="Merge" sortable>
      <template #body="slotProps">
        <RouterLink
          :to="`/yarns/${slotProps.data.id}`"
          class="font-medium text-neutral-800 block hover:underline"
        >
          {{ slotProps.data.mergeNumber }}
        </RouterLink>
      </template>
    </Column>
    <Column field="yarnType" header="Yarn Type" sortable>
      <template #body="slotProps">
        {{ getYarnType(slotProps.data.yarnType) }}
      </template>
    </Column>
    <Column field="count" header="Count" sortable>
      <template #body="slotProps">
        {{ slotProps.data.yarnCount }}
      </template>
    </Column>
    <Column field="supplier" header="Supplier" sortable>
      <template #body="slotProps">
        <RouterLink
          :to="`/suppliers/${slotProps.data.supplierId}`"
          class="font-medium text-neutral-800 block hover:underline"
        >
          {{ getSupplier(slotProps.data.supplierId, suppliers) }}
        </RouterLink>
      </template>
    </Column>
    <Column field="action">
      <template #body="slotProps">
        <RouterLink
          :to="`/yarns/${slotProps.data.id}`"
          class="flex items-center gap-x-2 group hover:text-primary-default"
        >
          <span> More Details</span>
          <Icon
            icon="material-symbols:more-up"
            class="text-2xl group-hover:translate-x-2 transition-transform text-primary-500"
          />
        </RouterLink>
      </template>
    </Column>

    <!------------------------------------------ pagination---------------------------------------------------->
    <template #paginatorprevpagelinkicon>
      <Button size="small" class="space-x-1 items-center" outlined severity="secondary">
        <Icon icon="material-symbols:chevron-left" class="text-2xl" />
        <span>Previous</span>
      </Button>
    </template>
    <template #paginatornextpagelinkicon>
      <Button size="small" outlined severity="secondary" class="space-x-1 items-center">
        <span>Next</span>
        <Icon icon="material-symbols:chevron-right" class="text-2xl" />
      </Button>
    </template>
  </DataTable>
</template>